/**
 * Product Helpers
 *
 * Products are first-class entities alongside companies. The product index
 * (data/productlistsearch.json, written by BDS productexport.py) is dev-only for
 * now: it loads at runtime from the dev data server when the PRODUCTS dev flag
 * is on, and production builds never request it.
 */
import { useEffect, useMemo, useState } from 'react';

import { getDevFlag } from '../constants/devFlags';
import { loadLocalOrFixtureJson, shouldUseLocalOrFixtureData } from './dataLoader';

export const PRODUCT_INDEX_PATH = 'data/productlistsearch.json';
export const MAX_PRODUCT_SEARCH_RESULTS = 8;

/** Products are enabled only in dev mode against local or fixture data. */
export const isProductsEnabled = () => getDevFlag('PRODUCTS') && shouldUseLocalOrFixtureData();

/** URL key for an offering; must match BDS shared.product_registry.product_key. */
export const productKeyForOffering = (offeringId) => String(offeringId || '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

export const productPath = (productKey, view) => (
  `/product/${productKey}${view ? `/${view}` : ''}`
);

let productIndexPromise = null;
export const resetProductIndexForTests = () => { productIndexPromise = null; };

export const loadProductIndex = () => {
  if (!isProductsEnabled()) return Promise.resolve([]);
  if (!productIndexPromise) {
    productIndexPromise = loadLocalOrFixtureJson(PRODUCT_INDEX_PATH)
      .then((rows) => (Array.isArray(rows) ? rows : []))
      .catch((error) => {
        productIndexPromise = null;
        throw error;
      });
  }
  return productIndexPromise;
};

export const buildProductLookups = (products) => {
  const byKey = new Map();
  const byOffering = new Map();
  const byOwnerTicker = new Map();
  const byFamily = new Map();
  (products || []).forEach((product) => {
    byKey.set(product.productKey, product);
    byOffering.set(product.offeringId, product);
    if (product.ownerTicker) {
      const ticker = String(product.ownerTicker).toUpperCase();
      if (!byOwnerTicker.has(ticker)) byOwnerTicker.set(ticker, []);
      byOwnerTicker.get(ticker).push(product);
    }
    if (product.familyId) {
      if (!byFamily.has(product.familyId)) byFamily.set(product.familyId, []);
      byFamily.get(product.familyId).push(product);
    }
  });
  return { products: products || [], byKey, byOffering, byOwnerTicker, byFamily };
};

const EMPTY_LOOKUPS = buildProductLookups([]);

/** The product index and lookups; empty (and never fetched) when products are disabled. */
export const useProductIndex = () => {
  const enabled = isProductsEnabled();
  const [state, setState] = useState({ products: [], loading: enabled, error: null });
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    loadProductIndex()
      .then((products) => { if (!cancelled) setState({ products, loading: false, error: null }); })
      .catch((error) => { if (!cancelled) setState({ products: [], loading: false, error }); });
    return () => { cancelled = true; };
  }, [enabled]);
  const lookups = useMemo(
    () => (state.products.length ? buildProductLookups(state.products) : EMPTY_LOOKUPS),
    [state.products],
  );
  return { ...lookups, loading: state.loading, error: state.error, enabled };
};

const text = (value) => String(value || '').toLowerCase();

/** Name, aliases and owner (name or public ticker) match the query. */
export const matchesProductSearch = (product, query) => {
  const normalized = text(query).trim();
  if (!normalized) return true;
  if (text(product?.name).includes(normalized)) return true;
  if ((product?.aliases || []).some((alias) => text(alias).includes(normalized))) return true;
  if (text(product?.ownerName).includes(normalized)) return true;
  const ticker = text(product?.ownerTicker);
  return Boolean(ticker) && !ticker.startsWith('pvt-') && ticker === normalized;
};

const searchRank = (product, query) => {
  const name = text(product.name);
  if (name === query) return 0;
  if (name.startsWith(query)) return 1;
  if (name.includes(query)) return 2;
  if ((product.aliases || []).some((alias) => text(alias).includes(query))) return 3;
  return 4;
};

/** Ranked product matches: exact and prefix name matches first, products before families. */
export const searchProducts = (products, query, limit = MAX_PRODUCT_SEARCH_RESULTS) => {
  const normalized = text(query).trim();
  if (!normalized) return [];
  return (products || [])
    .filter((product) => matchesProductSearch(product, normalized))
    .map((product) => ({ product, rank: searchRank(product, normalized) }))
    .sort((left, right) => (
      left.rank - right.rank
      || Number(left.product.offeringType !== 'product') - Number(right.product.offeringType !== 'product')
      || text(left.product.name).localeCompare(text(right.product.name))
    ))
    .slice(0, limit)
    .map(({ product }) => product);
};

/** Short owner label: the owning company, or its steward for benchmarks. */
export const productOwnerLabel = (product) => (
  product?.ownerName || product?.steward || (product?.isBenchmark ? 'Benchmark' : '')
);
